import React, {
    useState,
    useRef,
    useImperativeHandle,
    forwardRef,
} from "react";
import { Form } from "../../../../components/forms";
import UnifiedFileUpload from "../../../../components/forms/UnifiedFileUpload";
import Typography from "../../../../components/Typography/Typography";
import "./AIAssitPage.scss";

const UploadDocument = forwardRef(
    (
        {
            selectedAssetType,
            isExtracting,
            onFilesSelected,
            uploadedDocuments,
            selectedFiles = [],
        },
        ref,
    ) => {
        const [uploadedFiles, setUploadedFiles] = useState([]);
        const formRef = useRef(null);

        const initialValues = {
            documents: [],
        };

        const handleFileSelected = (files) => {
            const fileArray = files || [];
            setUploadedFiles(fileArray);
            onFilesSelected?.(fileArray);

            // Clear validation error when files are selected
            if (fileArray.length > 0 && formRef.current) {
                formRef.current.setFieldError("documents", null);
            }
        };

        // Sync with both uploadedDocuments and selectedFiles
        React.useEffect(() => {
            // Priority: selectedFiles (current state) > uploadedDocuments (cached from server)
            if (selectedFiles && selectedFiles.length > 0) {
                setUploadedFiles(selectedFiles);
            } else if (uploadedDocuments && uploadedDocuments.length > 0) {
                setUploadedFiles(uploadedDocuments);
            }
        }, [uploadedDocuments, selectedFiles]);

        const handleValidation = (values) => {
            const errors = {};

            if (!values.documents || values.documents.length === 0) {
                errors.documents = "Please upload at least one document";
            }

            return errors;
        };

        // Expose validation and file count methods to parent
        useImperativeHandle(ref, () => ({
            triggerValidation: () => {
                if (formRef.current && uploadedFiles.length === 0) {
                    formRef.current.setFieldError(
                        "documents",
                        "Please upload at least one document",
                    );
                    formRef.current.setFieldTouched("documents", true);
                    return false;
                }
                return true;
            },
            hasFiles: () => uploadedFiles.length > 0,
            getCurrentFiles: () => uploadedFiles,
        }));

        return (
            <div className="upload-document-container">
                <div className="mb-4">
                    <Typography variant="h3" className="mb-2">
                        Upload Documents for AI Processing
                    </Typography>
                    <Typography variant="body1" className="text-muted">
                        Upload up to 3 documents (PNG, JPG, JPEG, PDF) to
                        extract asset data using AI.
                    </Typography>

                    {selectedAssetType && (
                        <div className="alert alert-info mt-3">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Selected Asset Type:</strong>{" "}
                            {selectedAssetType.label}
                        </div>
                    )}
                </div>

                <Form
                    innerRef={formRef}
                    initialValues={initialValues}
                    validate={handleValidation}
                    className="upload-form"
                >
                    {/* Hide upload field during extraction */}
                    {!isExtracting && (
                        <div className="row">
                            <div className="col-12">
                                <UnifiedFileUpload
                                    name="documents"
                                    label={
                                        <div className="d-flex align-items-center justify-content-between w-100">
                                            <span>Upload Documents</span>
                                            <span className="badge bg-secondary rounded-pill ms-2">
                                                {uploadedFiles.length}
                                            </span>
                                        </div>
                                    }
                                    fileType="mixed"
                                    multiple={true}
                                    onFileSelected={handleFileSelected}
                                    selectedFile={uploadedFiles}
                                    initialFile={uploadedDocuments}
                                    width="100%"
                                    maxFileSize={5 * 1024 * 1024}
                                    maxFiles={3}
                                />
                            </div>
                        </div>
                    )}

                    <div className="row mt-4">
                        {/* <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="upload-status">
                                    {uploadedFiles.length > 0 &&
                                        !isExtracting && (
                                            <div className="alert alert-success">
                                                <i className="bi bi-check-circle me-2"></i>
                                                {uploadedFiles.length} document
                                                {uploadedFiles.length > 1
                                                    ? "s"
                                                    : ""}{" "}
                                                selected
                                            </div>
                                        )}
                                    {uploadError && !isExtracting && (
                                        <div className="alert alert-danger mt-2">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {uploadError}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div> */}

                        <div className="col-12">
                            <div className="upload-actions">
                                {isExtracting && (
                                    <div className="progress-indicator-card">
                                        <div className="progress-header">
                                            <div className="spinner-container">
                                                <div className="spinner"></div>
                                            </div>
                                            <div className="progress-text">
                                                <h5 className="progress-title">
                                                    Processing Documents
                                                </h5>
                                                <p className="progress-subtitle">
                                                    AI is extracting asset data
                                                    from your documents...
                                                </p>
                                                {uploadedFiles.length > 0 && (
                                                    <p className="text-muted small">
                                                        Processing{" "}
                                                        {uploadedFiles.length}{" "}
                                                        document
                                                        {uploadedFiles.length >
                                                        1
                                                            ? "s"
                                                            : ""}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="progress-bar-container">
                                            <div className="progress-bar-ai">
                                                <div className="progress-fill"></div>
                                            </div>
                                            <p className="progress-info">
                                                This may take a few moments
                                                depending on document complexity
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        );
    },
);

export default UploadDocument;
